/**
 * Confluence engine — computes early trend direction, strength, and NO TRADE reasons
 * from the combined analysis + gammaData payloads.
 * Pure function: no React dependency, no side effects.
 */

// Minimum |slope| per poll to count as a meaningful PCR trend.
// Below this threshold the movement is noise, not signal.
const PCR_TREND_THRESHOLD = 0.01;

export function computeConfluence(analysis, gammaData, emaData, vixDirection, pcrTrend = 0, gammaFlipCrossover = null, leadingStocksData = null) {
  if (!analysis) return { confluenceScore: 0, trendStrength: 'WEAK', earlyTrendDirection: 'NEUTRAL', noTradeReasons: [] };

  const {
    oiSetupSignal,
    ceOIMatrix,
    peOIMatrix,
    avgIV,
    ivRegime,
    ivSkew,
    atmCallIV,
    atmPutIV,
    pcr,
    callExits,
    putExits,
    maxPainPrice,
    value: spotFromAnalysis,
  } = analysis;

  const {
    bbSignal,
    volumeAlerts = [],
    gammaFlipLevel,
    marketRegime,
    spotPrice: spotFromGamma,
    callWallStrike,
    putWallStrike,
    bbPercentB,
    gammaBlastZone,
    daysToExpiry,
    ivRank,
    ivPercentile,
    thetaCallPct,
    thetaPutPct,
    straddleExpansionRatio,
    gapOpenPct,
    callWallConcentrationPct,
    putWallConcentrationPct,
    atmCallConcentrationPct,
    atmPutConcentrationPct,
    timeZone,
    // Chain sweep fields now computed server-side in GammaService and sent with GammaAnalysis.
    // Mirrors TradingSetupPanel chainSweepData: ≥40% of strikes aligned CE LB+PE SB (bull) or vice-versa.
    chainSweepBull = false,
    chainSweepBear = false,
  } = gammaData || {};

  // Guard against null from backend (destructuring default only covers undefined, not null)
  const safeVolumeAlerts = volumeAlerts ?? [];

  const spot = spotFromGamma || spotFromAnalysis;

  // Directional volume: only count spikes that align with each direction (#1)
  const volCallAligned = safeVolumeAlerts.some(a => a.sentiment === 'BULLISH');
  const volPutAligned  = safeVolumeAlerts.some(a => a.sentiment === 'BEARISH');

  // ── CALL score ─────────────────────────────────────────────────────────────
  let callScore = 0;

  // BB + OI setup cap: both respond to the same price-over-resistance event — on >80% of breakout
  // bars they co-occur, previously contributing up to 5 points from one market event. Cap at 3.
  const bbBullPoints = bbSignal === 'Bullish' ? 2 : 0;
  const oiBullPoints = oiSetupSignal === 'Strong Bullish' ? 3 : oiSetupSignal === 'Bullish' ? 2 : 0;
  callScore += Math.min(3, bbBullPoints + oiBullPoints);

  // CE: short-covering = old writers fleeing (classic); long build-up = new buyers confirming (momentum)
  if (ceOIMatrix === 'Short Covering' || ceOIMatrix === 'Long Build-up') callScore += 1;
  // PE: short build-up = writers adding support (classic); long unwinding = put holders giving up (momentum)
  if (peOIMatrix === 'Short Build-up' || peOIMatrix === 'Long Unwinding') callScore += 1;
  if (volCallAligned || chainSweepBull) callScore += 1;             // #1 directional vol or chain sweep
  // avgIV < dynamicIVLimit removed: added equal points to both callScore and putScore
  // (zero net directional value). IV environment is captured by condition 7 in the checklist.
  if (spot != null && gammaFlipLevel != null && spot > gammaFlipLevel) callScore += 1;

  // Gamma flip crossover UP: spot just crossed above flip level — dealers forced to buy aggressively
  // This is stronger than merely being above the flip (+1); the crossing event itself is the signal (+2)
  if (gammaFlipCrossover === 'UP') callScore += 2;

  // EMA regime: 20-period EMA trend confirmation (#3)
  if (emaData?.hasEnoughData) {
    if (emaData.regime === 'STRONG_BULL' || emaData.regime === 'BULL') callScore += 1;
  }

  // BB %B: above mid and not yet overbought → trend has room to run for calls
  if (bbPercentB != null && bbPercentB > 0.5 && bbPercentB <= 0.85) callScore += 1;

  // IV Rank for calls: cheap premium is a genuine edge only when VIX is NOT rising.
  // Rising VIX = vol expanding against calls (premium contraction risk for call buyers even if rank is low).
  // Declining or flat VIX = calm environment where cheap calls benefit from low theta drag.
  if (ivRank != null && ivRank < 30 && vixDirection !== 'rising') callScore += 1;

  // VIX direction: declining VIX = market calming = bullish confirmation for calls
  if (vixDirection === 'declining') callScore += 1;

  // PCR trend: rising PCR (put writers adding) = sustained bullish pressure over 5+ polls
  if (pcrTrend > PCR_TREND_THRESHOLD) callScore += 1;

  // Lead stocks: STRONG bullish alignment in heavyweight stocks before index moves = early edge
  // +2 for STRONG (all stocks aligned), +1 for MODERATE (majority aligned), -1 when opposing
  if (leadingStocksData) {
    if (leadingStocksData.compositeBias === 'BULLISH') {
      callScore += leadingStocksData.leadStrength === 'STRONG' ? 2 : 1;
    } else if (leadingStocksData.compositeBias === 'BEARISH') {
      callScore -= 1; // opposing signal — partial discount
    }
  }

  // IV skew: avg(OTM PE IV) − avg(OTM CE IV). Negative skew = call buyers paying premium = greed = bullish.
  // Threshold −3 filters out bid/ask spread noise. Independent of all other conditions.
  if (ivSkew != null && ivSkew < -3) callScore += 1;

  // ── PUT score ──────────────────────────────────────────────────────────────
  let putScore = 0;

  // Same cap applied on bear side — mirrors call scoring above.
  const bbBearPoints = bbSignal === 'Bearish' ? 2 : 0;
  const oiBearPoints = oiSetupSignal === 'Strong Bearish' ? 3 : oiSetupSignal === 'Bearish' ? 2 : 0;
  putScore += Math.min(3, bbBearPoints + oiBearPoints);

  // PE: short-covering = old writers fleeing (classic); long build-up = new buyers confirming (momentum)
  if (peOIMatrix === 'Short Covering' || peOIMatrix === 'Long Build-up') putScore += 1;
  // CE: short build-up = writers adding overhead (classic); long unwinding = call holders giving up (momentum)
  if (ceOIMatrix === 'Short Build-up' || ceOIMatrix === 'Long Unwinding') putScore += 1;
  if (volPutAligned || chainSweepBear) putScore += 1;              // #1 directional vol or chain sweep
  if (spot != null && gammaFlipLevel != null && spot < gammaFlipLevel) putScore += 1;

  // Gamma flip crossover DOWN: spot just crossed below flip level — dealers forced to sell aggressively
  if (gammaFlipCrossover === 'DOWN') putScore += 2;

  // EMA regime: 20-period EMA trend confirmation (#3)
  if (emaData?.hasEnoughData) {
    if (emaData.regime === 'STRONG_BEAR' || emaData.regime === 'BEAR') putScore += 1;
  }

  // BB %B: below mid and not yet oversold → trend has room to fall for puts
  if (bbPercentB != null && bbPercentB < 0.5 && bbPercentB >= 0.15) putScore += 1;

  // IV Rank for puts: cheap puts are only a genuine edge when VIX is rising.
  // Rising VIX + low IV rank = ideal put entry: cheap premium AND vol expanding in your favour.
  // Declining VIX + low IV rank = cheap puts but vol contraction will erode gains — no bonus.
  if (ivRank != null && ivRank < 30 && vixDirection === 'rising') putScore += 1;

  // VIX direction: rising VIX = fear building = bearish + premium expanding (double benefit for puts)
  if (vixDirection === 'rising') putScore += 1;

  // PCR trend: falling PCR (put OI shrinking or call builds surging) = sustained bearish pressure over 5+ polls
  if (pcrTrend < -PCR_TREND_THRESHOLD) putScore += 1;

  // Lead stocks: bearish alignment in heavyweights = early warning for puts
  if (leadingStocksData) {
    if (leadingStocksData.compositeBias === 'BEARISH') {
      putScore += leadingStocksData.leadStrength === 'STRONG' ? 2 : 1;
    } else if (leadingStocksData.compositeBias === 'BULLISH') {
      putScore -= 1; // opposing signal — partial discount
    }
  }

  // IV skew: positive skew = put buyers paying premium = fear = bearish. Threshold +3 filters noise.
  if (ivSkew != null && ivSkew > 3) putScore += 1;

  // ── Derive overall confluence ──────────────────────────────────────────────
  const confluenceScore = Math.min(10, Math.max(callScore, putScore));

  const earlyTrendDirection =
    callScore > putScore && confluenceScore >= 4 ? 'CALL' :
    putScore > callScore && confluenceScore >= 4 ? 'PUT' :
    'NEUTRAL';

  const trendStrength =
    confluenceScore >= 8 ? 'STRONG' :
    confluenceScore >= 6 ? 'MODERATE' :
    confluenceScore >= 4 ? 'FORMING' :
    'WEAK';

  // ── NO TRADE reasons ───────────────────────────────────────────────────────
  const reasons = [];

  // V1: VIX declining + CALL signal = direction ok but vol-crush warning
  if (vixDirection === 'declining' && earlyTrendDirection === 'CALL') {
    reasons.push('VIX declining — bullish direction confirmed, but IV contracting may erode call premium (vol-crush risk)');
  }

  // B1: BB %B extreme overbought — dangerous for call buyers (buying at the upper wall)
  if (bbPercentB != null && bbPercentB > 0.95) {
    reasons.push(`BB %B overbought (${(bbPercentB * 100).toFixed(0)}%) — spot at upper band, call buyers paying top premium`);
  } else if (bbPercentB != null && bbPercentB > 0.85 && earlyTrendDirection === 'CALL') {
    reasons.push(`BB %B approaching overbought (${(bbPercentB * 100).toFixed(0)}%) — limited upside room before band resistance`);
  }

  // B2: BB %B extreme oversold — dangerous for put buyers (buying at the lower wall)
  if (bbPercentB != null && bbPercentB < 0.05) {
    reasons.push(`BB %B oversold (${(bbPercentB * 100).toFixed(0)}%) — spot at lower band, put buyers paying top premium`);
  } else if (bbPercentB != null && bbPercentB < 0.15 && earlyTrendDirection === 'PUT') {
    reasons.push(`BB %B approaching oversold (${(bbPercentB * 100).toFixed(0)}%) — limited downside room before band support`);
  }

  // P1: IV very high (vol crush danger)
  if ((avgIV != null && avgIV >= 40) || ivRegime === 'Very High') {
    reasons.push('IV too high (≥40%) — severe vol-crush risk for option buyers');
  } else if ((avgIV != null && avgIV >= 30) || ivRegime === 'High') {
    // P2: IV elevated (only if P1 not already added)
    if (!reasons.some(r => r.startsWith('IV too high'))) {
      reasons.push('Elevated IV (≥30%) — premium expensive, vol-crush likely on reversals');
    }
  }

  // P3: Gamma-pinned (long gamma AND spot within 0.5% of flip)
  if (
    marketRegime === 'Long Gamma' &&
    spot != null && gammaFlipLevel != null &&
    Math.abs(spot - gammaFlipLevel) / spot < 0.005
  ) {
    reasons.push('Gamma-pinned — spot near GEX flip, low directional move expected');
  }

  // LS1: Lead stock bias opposes the trade direction — heavyweight stocks moving against the index signal
  if (leadingStocksData && leadingStocksData.leadStrength !== 'NEUTRAL') {
    const leadBull = leadingStocksData.compositeBias === 'BULLISH';
    const leadBear = leadingStocksData.compositeBias === 'BEARISH';
    if (earlyTrendDirection === 'CALL' && leadBear) {
      reasons.push(`Lead stocks BEARISH (${leadingStocksData.alignmentSummary}) — heavyweights moving against CALL direction. Stocks lead index by 1–3 min`);
    } else if (earlyTrendDirection === 'PUT' && leadBull) {
      reasons.push(`Lead stocks BULLISH (${leadingStocksData.alignmentSummary}) — heavyweights moving against PUT direction. Stocks lead index by 1–3 min`);
    }
  }

  // GFC1: Gamma flip crossover opposing the signal direction
  // A fresh DOWN crossover while looking at CALLs means dealers just flipped to aggressive selling — direct headwind.
  if (gammaFlipCrossover === 'DOWN' && earlyTrendDirection === 'CALL') {
    reasons.push('Gamma flip crossed DOWN — dealers now short gamma and selling. Active headwind against CALL direction');
  } else if (gammaFlipCrossover === 'UP' && earlyTrendDirection === 'PUT') {
    reasons.push('Gamma flip crossed UP — dealers now long gamma and buying. Active headwind against PUT direction');
  }

  // P4: GEX walls too tight
  if (
    callWallStrike != null && putWallStrike != null &&
    callWallStrike > putWallStrike && spot != null &&
    (callWallStrike - putWallStrike) < spot * 0.01
  ) {
    reasons.push('GEX walls tight — spot compressed into a narrow range');
  }

  // P5 / P6: Spot near max pain
  if (maxPainPrice != null && spot != null) {
    const painPct = Math.abs(spot - maxPainPrice) / spot;
    if (painPct < 0.005) {
      reasons.push('Spot within 0.5% of Max Pain — expiry pinning risk, avoid directional options');
    } else if (painPct < 0.01) {
      reasons.push('Spot within 1% of Max Pain — writers have incentive to pin price here');
    }
  }

  // P7: OI–BB conflict
  const oiBullish = oiSetupSignal === 'Bullish' || oiSetupSignal === 'Strong Bullish';
  const oiBearish = oiSetupSignal === 'Bearish' || oiSetupSignal === 'Strong Bearish';
  if ((oiBullish && bbSignal === 'Bearish') || (oiBearish && bbSignal === 'Bullish')) {
    reasons.push('Conflicting signals — OI setup and BB direction disagree');
  }

  // P8: Both sides exiting
  if (callExits === 'Yes' && putExits === 'Yes') {
    reasons.push('Both sides exiting — no conviction, avoid new positions');
  }

  // P9: PCR extreme
  if (pcr != null) {
    if (pcr < 0.6) {
      reasons.push('PCR over-bearish (<0.6) — crowd one-sided bearish, contrarian caution');
    } else if (pcr > 1.4) {
      reasons.push('PCR over-bullish (>1.4) — crowd one-sided bullish, contrarian caution');
    }
  }

  // P9b: PCR trend strongly opposing the signal direction (≥3× the noise threshold)
  // A PUT direction signal while PCR has been rising for 5 polls = put writers adding, not fleeing.
  // A CALL direction signal while PCR is falling = put buyers retreating but no bullish conviction.
  const strongTrend = Math.abs(pcrTrend) >= PCR_TREND_THRESHOLD * 3;
  if (strongTrend) {
    if (pcrTrend > 0 && earlyTrendDirection === 'PUT') {
      reasons.push(`PCR rising trend (+${pcrTrend.toFixed(3)}/poll) — put writers adding support, opposes PUT signal`);
    } else if (pcrTrend < 0 && earlyTrendDirection === 'CALL') {
      reasons.push(`PCR falling trend (${pcrTrend.toFixed(3)}/poll) — put OI contracting or calls building, opposes CALL signal`);
    }
  }

  // P10: Put skew extreme
  if (ivSkew != null && ivSkew > 8) {
    reasons.push('Put skew extreme (>8%) — puts expensive, downside already priced in');
  }

  // P11: Call skew extreme
  if (ivSkew != null && ivSkew < -8) {
    reasons.push('Call skew extreme (<-8%) — calls expensive relative to puts');
  }

  // P12: ATM IV spread too wide
  if (atmCallIV != null && atmPutIV != null && Math.abs(atmCallIV - atmPutIV) > 5) {
    reasons.push('ATM IV spread wide (>5%) — one side pricing a specific directional move');
  }

  // P13 & P14: Only append on low-conviction setups
  if (confluenceScore < 5) {
    if (marketRegime === 'Long Gamma' && spot != null && gammaFlipLevel != null && spot > gammaFlipLevel * 1.01) {
      if (!reasons.some(r => r.startsWith('Gamma-pinned'))) {
        reasons.push('Long Gamma regime — dealers hedge against moves, directional moves get muted');
      }
    }
    const dirAlignedVol =
      earlyTrendDirection === 'CALL' ? (volCallAligned || chainSweepBull) :
      earlyTrendDirection === 'PUT'  ? (volPutAligned  || chainSweepBear) :
      safeVolumeAlerts.length > 0;
    if (!dirAlignedVol) {
      const hasAny = safeVolumeAlerts.length > 0;
      reasons.push(hasAny
        ? `Volume spikes present but none aligned with ${earlyTrendDirection} direction — opposing participation`
        : 'No volume confirmation — trend not yet validated by participation'
      );
    }
  }

  // T1: Time-of-day filter
  if (timeZone === 'AVOID_OPEN') {
    reasons.push('Market open zone (9:15–9:45) — price discovery chaotic, spreads wide. Wait for 9:45 before entering');
  } else if (timeZone === 'AVOID_CLOSE') {
    reasons.push('End-of-day zone (15:10–15:40) — unwinding pressure, theta burns fast. Avoid new entries');
  }

  // I1: IV Rank — options expensive relative to recent history
  if (ivRank != null && ivRank >= 70) {
    reasons.push(`IV Rank high (${ivRank.toFixed(0)}%) — options in top 30% of 60-day range. Buyers overpaying for premium`);
  } else if (ivRank != null && ivRank >= 50) {
    reasons.push(`IV Rank above median (${ivRank.toFixed(0)}%) — options above average cost, vol-crush risk elevated`);
  }

  // I2: IV Percentile
  if (ivPercentile != null && ivPercentile >= 75) {
    reasons.push(`IV Percentile ${ivPercentile.toFixed(0)}% — IV higher than ${ivPercentile.toFixed(0)}% of recent sessions`);
  }

  // I3: Low IV rank but PUT direction without vol expansion tailwind
  // Cheap puts are not enough — without VIX rising, theta + vol-crush erode the premium faster than the move.
  if (ivRank != null && ivRank < 30 && earlyTrendDirection === 'PUT' && vixDirection !== 'rising') {
    reasons.push(
      `IV Rank low (${ivRank.toFixed(0)}%) but VIX ${vixDirection === 'declining' ? 'declining' : 'flat'} — puts are cheap yet vol is not expanding. Theta drag without a vol tailwind reduces edge`
    );
  }

  // I4: Low IV rank but CALL direction with rising VIX
  // Cheap calls look attractive but rising VIX = IV is expanding against calls (vega negative impact on call value).
  if (ivRank != null && ivRank < 30 && earlyTrendDirection === 'CALL' && vixDirection === 'rising') {
    reasons.push(
      `IV Rank low (${ivRank.toFixed(0)}%) but VIX rising — calls are cheap but expanding IV works against call buyers (put premium benefits more from VIX spikes)`
    );
  }

  // S1: Straddle expansion — move already priced in
  if (straddleExpansionRatio != null && straddleExpansionRatio >= 1.3) {
    reasons.push(`Straddle expanded ${((straddleExpansionRatio - 1) * 100).toFixed(0)}% from open — premium surge already happened, buying now is chasing`);
  } else if (straddleExpansionRatio != null && straddleExpansionRatio >= 1.2) {
    reasons.push(`Straddle up ${((straddleExpansionRatio - 1) * 100).toFixed(0)}% from open — significant expansion, enter only on strong confluence`);
  }

  // G2: Gap open — dangerous for first 15 min
  if (gapOpenPct != null && Math.abs(gapOpenPct) >= 0.5) {
    const dir = gapOpenPct > 0 ? 'gap up' : 'gap down';
    reasons.push(`${dir} of ${Math.abs(gapOpenPct).toFixed(2)}% from yesterday's close — gap traps common, wait for 9:45 confirmation`);
  }

  // TH1: Theta/premium carry cost too high
  const thetaHigh = (thetaCallPct != null && thetaCallPct > 2.5) || (thetaPutPct != null && thetaPutPct > 2.5);
  if (thetaHigh) {
    const ce = thetaCallPct != null ? `CE ${thetaCallPct.toFixed(1)}%` : '';
    const pe = thetaPutPct  != null ? `PE ${thetaPutPct.toFixed(1)}%`  : '';
    reasons.push(`Theta carry cost high (${[ce, pe].filter(Boolean).join(' / ')}/day) — daily decay eats >2.5% of premium, holding costs too much`);
  }

  // C1: Call wall concentration — pin risk from writers
  if (callWallConcentrationPct != null && callWallConcentrationPct > 25 && earlyTrendDirection === 'CALL') {
    reasons.push(`${callWallConcentrationPct.toFixed(0)}% of call OI concentrated at call wall ${callWallStrike} — heavy writer defence, hard to break through`);
  }

  // C2: Put wall concentration — pin risk from writers
  if (putWallConcentrationPct != null && putWallConcentrationPct > 25 && earlyTrendDirection === 'PUT') {
    reasons.push(`${putWallConcentrationPct.toFixed(0)}% of put OI concentrated at put wall ${putWallStrike} — heavy writer defence, hard to break through`);
  }

  // AC1: ATM call OI concentration — writers defending the at-the-money strike
  // > 40% is extreme; > 25% is elevated. Either way bad for directional buyers.
  if (atmCallConcentrationPct != null && atmCallConcentrationPct > 40) {
    reasons.push(
      `${atmCallConcentrationPct.toFixed(0)}% of all call OI is at ATM — extreme writer concentration at current spot. ` +
      `Path of least resistance is sideways; directional breakout unlikely`
    );
  } else if (atmCallConcentrationPct != null && atmCallConcentrationPct > 25) {
    const dir = earlyTrendDirection === 'CALL' ? ' — opposes CALL direction' : '';
    reasons.push(
      `${atmCallConcentrationPct.toFixed(0)}% of call OI at ATM${dir}. Writers defending current level, sideways pressure elevated`
    );
  }

  // AC2: ATM put OI concentration — put writers anchoring the level from below
  if (atmPutConcentrationPct != null && atmPutConcentrationPct > 40) {
    reasons.push(
      `${atmPutConcentrationPct.toFixed(0)}% of all put OI is at ATM — extreme put writer concentration at current spot. ` +
      `Writers defending this level; breakdown requires significant force to dislodge`
    );
  } else if (atmPutConcentrationPct != null && atmPutConcentrationPct > 25) {
    const dir = earlyTrendDirection === 'PUT' ? ' — opposes PUT direction' : '';
    reasons.push(
      `${atmPutConcentrationPct.toFixed(0)}% of put OI at ATM${dir}. Put writers concentrated at spot level, breakdown more difficult`
    );
  }

  // G1: Gamma blast zone — expiry day, last 60 min before 15:40 IST
  // Gamma is exponentially elevated; option buyers face explosive time-decay + bid-ask blowout.
  if (gammaBlastZone) {
    reasons.push('⚠️ Gamma Blast Zone — expiry day, <60 min to close. Gamma is exponential; avoid new option buys, square off open positions');
  } else if (daysToExpiry != null && daysToExpiry === 0) {
    reasons.push('Expiry day — theta decay accelerating all day; prefer closing existing positions over new entries');
  }

  return { confluenceScore, trendStrength, earlyTrendDirection, noTradeReasons: reasons };
}
